import { ENDPOINTS } from '../endpoints';
import {
  CreateOrganizationDto,
  CreateOrganizationUserDto,
  CreateFarmLocationDto,
  OrganizationDto,
  OrganizationUserDto,
  FarmLocationDto,
  UpdateOrganizationDto,
  UpdateOrganizationUserDto,
  UpdateFarmLocationDto,
  OrganizationSearchParams,
  OrganizationSearchResponse,
  OrganizationInsertDto,
  OrganizationBatchResponseDto,
  OrganizationCheckRequestDto,
  OrganizationExistenceCheckDto,
  BatchUpsertRequestDto,
  BatchOperationResultDto,
  OrganizationExportDto,
} from '../types/dtos';
import { BaseService } from './base.service';
import { CustomResponseEntity } from '../types/common';
import { api } from '../client/axiosClient';

export class OrganizationService extends BaseService<OrganizationDto, CreateOrganizationDto, UpdateOrganizationDto> {
  constructor() {
    super(ENDPOINTS.organizations.base());
  }

  // Custom methods specific to organizations
  async findOwn(): Promise<CustomResponseEntity<OrganizationDto[]>> {
    return api.get(ENDPOINTS.organizations.own());
  }

  async findAllFarms(): Promise<CustomResponseEntity<FarmLocationDto[]>> {
    return api.get(ENDPOINTS.organizations.farms());
  }

  async searchChildOrganizations(params: OrganizationSearchParams): Promise<CustomResponseEntity<OrganizationSearchResponse>> {
    const queryParams = new URLSearchParams();
    
    // Add optional params
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.parentOrganizationId) queryParams.append('parentOrganizationId', params.parentOrganizationId);
    if (params.farmerLocationCity) queryParams.append('farmerLocationCity', params.farmerLocationCity);
    if (params.farmerLocationState) queryParams.append('farmerLocationState', params.farmerLocationState);
    if (params.farmerLocationCountry) queryParams.append('farmerLocationCountry', params.farmerLocationCountry);
    if (params.farmerLocationMinLatitude) queryParams.append('farmerLocationMinLatitude', params.farmerLocationMinLatitude.toString());
    if (params.farmerLocationMaxLatitude) queryParams.append('farmerLocationMaxLatitude', params.farmerLocationMaxLatitude.toString());
    if (params.farmerLocationMinLongitude) queryParams.append('farmerLocationMinLongitude', params.farmerLocationMinLongitude.toString());
    if (params.farmerLocationMaxLongitude) queryParams.append('farmerLocationMaxLongitude', params.farmerLocationMaxLongitude.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.size) queryParams.append('size', params.size.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    return api.get(`${ENDPOINTS.organizations.search()}?${queryParams.toString()}`);
  }

  // Backend returns the array directly, not wrapped in CustomResponseEntity
  async batchInsertOrganizations(
    parentOrgId: string, 
    organizations: OrganizationInsertDto[]
  ): Promise<OrganizationInsertDto[]> {
    return api.post(ENDPOINTS.organizations.batchCreate(parentOrgId), organizations);
  }

  async checkExistingOrganizations(
    parentOrgId: string,
    request: OrganizationCheckRequestDto
  ): Promise<OrganizationExistenceCheckDto> {
    return api.post(ENDPOINTS.organizations.checkExisting(parentOrgId), request);
  }

  async batchUpsertOrganizations(
    parentOrgId: string,
    request: BatchUpsertRequestDto
  ): Promise<BatchOperationResultDto> {
    return api.post(ENDPOINTS.organizations.batchUpsert(parentOrgId), request);
  }

  async batchDeleteOrganizations(
    parentOrgId: string,
    request: OrganizationCheckRequestDto
  ): Promise<BatchOperationResultDto> {
    return api.post(ENDPOINTS.organizations.batchDelete(parentOrgId), request);
  }

  // Backend returns the array directly, not wrapped in CustomResponseEntity
  async exportOrganizations(parentOrgId: string): Promise<OrganizationExportDto[]> {
    return api.get(ENDPOINTS.organizations.export(parentOrgId));
  }

  async addUserToOrganization(
    organizationId: string,
    userId: string
  ): Promise<CustomResponseEntity<any>> {
    return api.post(ENDPOINTS.organizationUsers.addUserToOrganization(organizationId), {
      userId: userId
    });
  }

  // Método para crear organizaciones principales usando el endpoint /parent
  async createParent(data: CreateOrganizationDto): Promise<CustomResponseEntity<OrganizationBatchResponseDto>> {
    const response = await api.post(ENDPOINTS.organizations.parents(), data);
    return response as CustomResponseEntity<OrganizationBatchResponseDto>;
  }

  // Método para obtener organizaciones principales (main=true, sin parent)
  async getMainOrganizations(): Promise<OrganizationDto[]> {
    const response = await api.get(ENDPOINTS.organizations.parents());
    const responseData = (response as any).data;
    
    // Los datos están directamente en response.data como propiedades numeradas
    const organizations = Object.keys(responseData || {})
      .filter(key => !isNaN(Number(key))) // Solo claves numéricas
      .map(key => responseData[key]) // Extraer cada organización
      .filter(org => org && typeof org === 'object' && org.id); // Filtrar objetos válidos
    
    return organizations;
  }
}

export class OrganizationUserService extends BaseService<
  OrganizationUserDto,
  CreateOrganizationUserDto,
  UpdateOrganizationUserDto
> {
  constructor() {
    super(ENDPOINTS.organizationUsers.base());
  }
}
export class FarmLocationService extends BaseService<FarmLocationDto, CreateFarmLocationDto, UpdateFarmLocationDto> {
  constructor(organizationId: string) {
    super(ENDPOINTS.farmLocations.base(organizationId));
  }
}

// Service instances
export const organizationService = new OrganizationService();
export const organizationUserService = new OrganizationUserService();

export interface OrganizationListOwnerDto {
  id: string;
  name: string;
  phones: string[];
}

export interface OrganizationListDto {
  id: string;
  idNumber: string;
  name: string;
  groupNames: string[];
  owner: OrganizationListOwnerDto;
  createdAt: string;
  updatedAt: string;
}

export type DateField = 'createdAt' | 'updatedAt';
export type DateCondition = 'after' | 'before' | 'on' | 'between';

export interface OrganizationListParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  dateField?: DateField;
  dateCondition?: DateCondition;
  dateFrom?: string;
  dateTo?: string;
}

export interface PaginatedOrganizationListResponse {
  data: {
    content: OrganizationListDto[];
    totalElements: number;
    totalPages: number;
    page: number;
    size: number;
    message?: string;
    type?: string;
  };
}

/**
 * Fetch organizations list (non-paginated - legacy)
 * @deprecated Use fetchOrganizationsListPaginated instead for better performance
 */
export const fetchOrganizationsList = async (parentOrganizationId: string): Promise<OrganizationListDto[]> => {
  const res = await api.get<{ data: OrganizationListDto[] }>(`/api/v1/organizations/list?parentOrganizationId=${parentOrganizationId}`);
  return res.data;
};

/**
 * Fetch organizations list with pagination and search support
 * 
 * @param parentOrganizationId - Parent organization ID
 * @param params - Pagination and search parameters
 * @returns Paginated organization list response
 */
export const fetchOrganizationsListPaginated = async (
  parentOrganizationId: string,
  params: OrganizationListParams = {}
): Promise<PaginatedOrganizationListResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('parentOrganizationId', parentOrganizationId);
  
  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }
  if (params.size !== undefined) {
    queryParams.append('size', params.size.toString());
  }
  if (params.searchTerm) {
    queryParams.append('searchTerm', params.searchTerm);
  }
  if (params.dateField) {
    queryParams.append('dateField', params.dateField);
  }
  if (params.dateCondition) {
    queryParams.append('dateCondition', params.dateCondition);
  }
  if (params.dateFrom) {
    queryParams.append('dateFrom', params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.append('dateTo', params.dateTo);
  }
  
  const queryString = queryParams.toString();
  const url = `/api/v1/organizations/list?${queryString}`;
  
  const response = await api.get<any>(url);
  
  // Handle response structure: data.content contains the array
  if (response && typeof response === 'object') {
    // Check if response has data.content structure
    if (response.data && response.data.content && Array.isArray(response.data.content)) {
      return {
        data: {
          content: response.data.content,
          totalElements: response.data.totalElements || response.data.content.length,
          totalPages: response.data.totalPages || Math.ceil((response.data.totalElements || response.data.content.length) / (params.size || 50)),
          page: response.data.page !== undefined ? response.data.page : (params.page || 0),
          size: response.data.size !== undefined ? response.data.size : (params.size || 50),
          message: response.data.message,
          type: response.data.type,
        },
      } as PaginatedOrganizationListResponse;
    }
    // Fallback: if response.data is directly an array (legacy support)
    if (Array.isArray(response.data)) {
      return {
        data: {
          content: response.data,
          totalElements: response.totalElements || response.data.length,
          totalPages: response.totalPages || Math.ceil((response.totalElements || response.data.length) / (params.size || 50)),
          page: response.page !== undefined ? response.page : (params.page || 0),
          size: response.size !== undefined ? response.size : (params.size || 50),
          message: response.message,
          type: response.type,
        },
      } as PaginatedOrganizationListResponse;
    }
  }
  
  // Fallback: return empty structure
  return {
    data: {
      content: [],
      totalElements: 0,
      totalPages: 0,
      page: params.page || 0,
      size: params.size || 50,
      message: 'No data',
      type: 'OK',
    },
  } as PaginatedOrganizationListResponse;
}; 